if (!customElements.get('product-info')) {
    customElements.define(
      'product-info',
      class ProductInfo extends HTMLElement {
        quantityInput = undefined;
        quantityForm = undefined;
        quantityInputSticky = undefined;
        quantityFormSticky = undefined;
        onVariantChangeUnsubscriber = undefined;
        cartUpdateUnsubscriber = undefined;
        abortController = undefined;
        pendingRequestUrl = null;
        preProcessHtmlCallbacks = [];
        postProcessHtmlCallbacks = [];
  
        constructor() {
          super();
  
          this.quantityInput = this.querySelector('.quantity__input-primary');
          this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
        }
  
        connectedCallback() {
          this.initializeProductSwapUtility();
  
          this.onVariantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.optionValueSelectionChange,
            this.handleOptionValueChange.bind(this)
          );
  
          this.initQuantityHandlers();
          
          // Initialize backorder message block on page load
          // Use setTimeout to ensure variant ID is available
          setTimeout(() => {
            if (window.backorderMessaging) {
              // Get variant ID from URL parameter first, then form input
              const urlParams = new URLSearchParams(window.location.search);
              let currentVariantId = urlParams.get('variant');
              
              if (!currentVariantId) {
                currentVariantId = this.productForm?.variantIdInput?.value;
              }
              
              const productLevelBackorder = window.backorderMessaging.productLevel || null;
              
              // Try both string and number format for variant ID
              let variantLevelBackorder = null;
              if (currentVariantId) {
                variantLevelBackorder = window.backorderMessaging.variants?.[currentVariantId] || 
                                       window.backorderMessaging.variants?.[parseInt(currentVariantId)] || 
                                       null;
              }
              
              // Update backorder message block (always call to ensure proper show/hide)
              this.updateBackorderMessageBlock(productLevelBackorder, variantLevelBackorder);
              
              // Update backorder input in forms
              const variantIdInput = this.productForm?.variantIdInput;
              if (variantIdInput && currentVariantId) {
                // Create a mock variant object for initialization
                const mockVariant = { id: currentVariantId };
                this.updateBackorderInput(mockVariant);
              }
            }
          }, 100);
          
          this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        }
  
        addPreProcessCallback(callback) {
          this.preProcessHtmlCallbacks.push(callback);
        }
  
        initQuantityHandlers() {
          if (!this.quantityInput && !this.quantityInputSticky) return;
  
          this.quantityForm = this.querySelector('.product-form__quantity-primary');
          this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
          if (!this.quantityForm && !this.quantityFormSticky) return;
  
          this.setQuantityBoundries();
          if (!this.dataset.originalSection) {
            this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
          }
        }
  
        disconnectedCallback() {
          this.onVariantChangeUnsubscriber();
          this.cartUpdateUnsubscriber?.();
        }
  
        initializeProductSwapUtility() {
          this.preProcessHtmlCallbacks.push((html) =>
            html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
          );
          this.postProcessHtmlCallbacks.push((newNode) => {
            window?.Shopify?.PaymentButton?.init();
            window?.ProductModel?.loadShopifyXR();
          });
        }
  
        handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
          if (!this.contains(event.target)) return;
  
          this.resetProductFormState();
  
          const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
          this.pendingRequestUrl = productUrl;
          const shouldSwapProduct = (this.dataset.url !== productUrl) || this.querySelector('li[data-gang-option]') !== null;
          const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
  
          this.renderProductInfo({
            requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
            targetId: target.id,
            callback: shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl),
          });
        }
  
        resetProductFormState() {
          const productForm = this.productForm;
          productForm?.toggleSubmitButton(true);
          productForm?.handleErrorMessage();
        }
  
        handleSwapProduct(productUrl, updateFullPage) {
          return (html) => {
            this.productModal?.remove();
  
            const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
            const variant = this.getSelectedVariant(html.querySelector(selector));
            this.updateURL(productUrl, variant?.id);
  
            if (updateFullPage) {
              document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
  
              HTMLUpdateUtility.viewTransition(
                document.querySelector('main'),
                html.querySelector('main'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            } else {
              HTMLUpdateUtility.viewTransition(
                this,
                html.querySelector('product-info'),
                this.preProcessHtmlCallbacks,
                this.postProcessHtmlCallbacks
              );
            }
          };
        }
  
        renderProductInfo({ requestUrl, targetId, callback }) {
          this.abortController?.abort();
          this.abortController = new AbortController();
  
          fetch(requestUrl, { signal: this.abortController.signal })
            .then((response) => response.text())
            .then((responseText) => {
              this.pendingRequestUrl = null;
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              callback(html);
            })
            .then(() => {
              // set focus to last clicked option value
              document.querySelector(`#${targetId}`)?.focus();
            })
            .catch((error) => {
              if (error.name === 'AbortError') {
                console.log('Fetch aborted by user');
              } else {
                console.error(error);
              }
            });
        }
  
        getSelectedVariant(productInfoNode) {
          const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
          return !!selectedVariant ? JSON.parse(selectedVariant) : null;
        }
  
        buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
          const params = [];
  
          !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);
  
          if (optionValues.length) {
            params.push(`option_values=${optionValues.join(',')}`);
          }
  
          return `${url}?${params.join('&')}`;
        }
  
        updateOptionValues(html) {
          const variantSelects = html.querySelector('variant-selects[data-primary]');
          if (variantSelects) {
            HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
          }
          const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
          if (variantSelectsAlt) {
            HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
          }
        }
  
                // New method to handle backorder elements
        handleBackorderMetafield(variant) {
          // Hide all backorder elements first
          document.querySelectorAll('[data-backorder-variant]').forEach(element => {
            element.style.display = 'none';
          });

          if (!variant) return;

          // Show elements specific to this variant ID
          const variantBackorderElements = document.querySelectorAll(`[data-backorder-variant="${variant.id}"]`);
          
          if (variantBackorderElements.length > 0) {
            variantBackorderElements.forEach(element => {
              element.style.display = 'block';
            });

            console.log(`Showing backorder elements for variant ${variant.id}`);
          }

          // NOTE: Keeping original behavior - not updating inputs dynamically
          // this.updateBackorderInput(variant);
        }

        // Method to handle variant-specific backorder input
        updateBackorderInput(variant) {
          // Find all product forms in this product-info element
          const productForms = this.querySelectorAll('form[data-type="add-to-cart-form"]');
          
          // Get backorder messages
          const productLevelBackorder = window.backorderMessaging?.productLevel || null;
          // Try both string and number format for variant ID
          const variantLevelBackorder = variant?.id ? (
            window.backorderMessaging?.variants?.[variant.id] || 
            window.backorderMessaging?.variants?.[parseInt(variant.id)] || 
            null
          ) : null;
          
          // Priority: Product-level takes precedence
          const backorderValue = productLevelBackorder || variantLevelBackorder || '';
          
          productForms.forEach(form => {
            // Remove any existing backorder input (check both possible names)
            const existingBackorderInput = form.querySelector('input[name="properties[Note]"]') || 
                                           form.querySelector('input[name="properties[Backorder Note]"]');
            if (existingBackorderInput) {
              existingBackorderInput.remove();
            }

            // Add new input if there's a message
            // Only update if the value is different to avoid unnecessary DOM changes
            if (backorderValue) {
              const backorderInput = document.createElement('input');
              backorderInput.type = 'hidden';
              backorderInput.name = 'properties[Note]';
              // Trim and clean the value, and escape any special characters that might break Monster Cart
              const cleanValue = backorderValue.trim().replace(/\s+/g, ' ').replace(/["'<>]/g, '');
              backorderInput.value = cleanValue;
              
              // Add it after the variant ID input
              const variantIdInput = form.querySelector('input[name="id"]');
              if (variantIdInput) {
                variantIdInput.insertAdjacentElement('afterend', backorderInput);
              } else {
                form.appendChild(backorderInput);
              }
            }
          });
        }
  
        // Method to update backorder message block
        updateBackorderMessageBlock(productLevelBackorder, variantLevelBackorder) {
          // Try to find the backorder block using the section ID
          const backorderBlock = this.querySelector(`#BackorderMessage-${this.dataset.section}`);
          if (!backorderBlock) {
            // If not found, try finding by class as fallback
            const backorderBlockByClass = this.querySelector('.backorder-message-block');
            if (!backorderBlockByClass) return;
            this.updateBackorderBlockContent(backorderBlockByClass, productLevelBackorder, variantLevelBackorder);
            return;
          }

          this.updateBackorderBlockContent(backorderBlock, productLevelBackorder, variantLevelBackorder);
        }

        // Helper method to update backorder block content
        updateBackorderBlockContent(backorderBlock, productLevelBackorder, variantLevelBackorder) {
          const messageParagraph = backorderBlock.querySelector('p');
          if (!messageParagraph) return;

          // Priority: Product-level takes precedence
          if (productLevelBackorder) {
            messageParagraph.textContent = productLevelBackorder;
            backorderBlock.style.display = '';
          } else if (variantLevelBackorder) {
            // Only show variant-level if product-level is not set
            messageParagraph.textContent = variantLevelBackorder;
            backorderBlock.style.display = '';
          } else {
            // Hide if no messages
            backorderBlock.style.display = 'none';
          }
        }

        handleUpdateProductInfo(productUrl) {
  
          return (html) => {
            const variant = this.getSelectedVariant(html);
  
            // Get backorder messages for variant change
            const productLevelBackorder = window.backorderMessaging?.productLevel || null;
            // Try both string and number format for variant ID
            const variantLevelBackorder = variant?.id ? (
              window.backorderMessaging?.variants?.[variant.id] || 
              window.backorderMessaging?.variants?.[parseInt(variant.id)] || 
              null
            ) : null;

            // Update backorder message block
            this.updateBackorderMessageBlock(productLevelBackorder, variantLevelBackorder);

            // Update backorder input in forms
            this.updateBackorderInput(variant);

            // Handle backorder metafield (visual elements only)
            this.handleBackorderMetafield(variant);
  
            this.pickupAvailability?.update(variant);
            this.updateOptionValues(html);
            this.updateURL(productUrl, variant?.id);
            this.updateVariantInputs(variant?.id);
  
            if (!variant) {
              this.setUnavailable();
              return;
            }
  
            this.updateMedia(html, variant?.featured_media?.id);
  
            const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
              const source = html.getElementById(`${id}-${this.sectionId}`);
              const destination = this.querySelector(`#${id}-${this.dataset.section}`);
              if (source && destination) {
                destination.innerHTML = source.innerHTML;
                destination.classList.toggle('hidden', shouldHide(source));
              }
            };
  
            updateSourceFromDestination('price');
            updateSourceFromDestination('Price-Alt');
            updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
            updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
            updateSourceFromDestination('Volume');
            updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
  
            const countdownPriceEl = this.querySelector('#countdown-price');
            if (variant && variant.compare_at_price && variant.compare_at_price > variant.price) {
              countdownPriceEl?.classList.remove('hidden');
            } else {
              countdownPriceEl?.classList.add('hidden');
            }
            
            this.updateQuantityRules(this.sectionId, html);
            this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
            this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');
  
            this.productForm?.toggleSubmitButton(
              html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
              theme.variantStrings.soldOut
            );
  
            publish(PUB_SUB_EVENTS.variantChange, {
              data: {
                sectionId: this.sectionId,
                html,
                variant,
              },
            });
          };
        }
  
        updateVariantInputs(variantId) {
          this.querySelectorAll(
            `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
          ).forEach((productForm) => {
            const input = productForm.querySelector('input[name="id"]');
            input.value = variantId ?? '';
            input.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }
  
        updateURL(url, variantId) {
          this.querySelector('share-button')?.updateUrl(
            `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
          );
  
          if (this.dataset.updateUrl === 'false') return;
          window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
        }
  
                setUnavailable() {
          this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

          const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
            .map((id) => `#${id}-${this.dataset.section}`)
            .join(', ');
          document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));

          // Hide backorder elements when variant is unavailable
          document.querySelectorAll('[data-backorder-variant]').forEach(element => {
            element.style.display = 'none';
          });

          // Remove backorder inputs when variant is unavailable
          this.updateBackorderInput(null);
        }
  
        updateMedia(html, variantFeaturedMediaId) {
          if (!variantFeaturedMediaId) return;
          const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
          const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);
  
          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
            const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
            const sourceMap = new Map(
              mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
            );
            return [mediaGallerySourceItems, sourceSet, sourceMap];
          };
  
          if (mediaGallerySource && mediaGalleryDestination) {
            let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            const mediaGalleryDestinationItems = Array.from(
              mediaGalleryDestination.querySelectorAll('li[data-media-id]')
            );
            const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
            let shouldRefresh = false;
  
            // add items from new data not present in DOM
            for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
              if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
                mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
                shouldRefresh = true;
              }
            }
  
            // remove items from DOM not present in new data
            for (let i = 0; i < mediaGallerySourceItems.length; i++) {
              if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
                mediaGallerySourceItems[i].remove();
                shouldRefresh = true;
              }
            }
  
            // refresh
            if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
  
            // if media galleries don't match, sort to match new data order
            mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);
  
              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
                );
  
                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
              }
            });
          }
  
          // set featured media as active in the media gallery
          const mediaGallery = this.querySelector('media-gallery');
          if (mediaGallery != null) {
            mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
          } else {
            this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
          }
  
          // update media modal
          const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
          const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
          if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
        }
  
        setQuantityBoundries() {
          const input = this.quantityInput || this.quantityInputSticky;
          const data = {
            cartQuantity: input?.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
            min: input?.dataset.min ? parseInt(input.dataset.min) : 1,
            max: input?.dataset.max ? parseInt(input.dataset.max) : null,
            step: input?.step ? parseInt(input.step) : 1,
          };
  
          let min = data.min;
          const max = data.max === null ? data.max : data.max - data.cartQuantity;
          if (max !== null) min = Math.min(min, max);
          if (data.cartQuantity >= data.min) min = Math.min(min, data.step);
  
          if(this.quantityInput) this.quantityInput.min = min;
          if(this.quantityInputSticky) this.quantityInputSticky.min = min;
  
          if (max) {
            if(this.quantityInput) this.quantityInput.max = max;
            if(this.quantityInputSticky) this.quantityInputSticky.max = max;
          } else {
            this.quantityInput?.removeAttribute('max');
            this.quantityInputSticky?.removeAttribute('max');
          }
          if(this.quantityInput) this.quantityInput.value = min;
          if(this.quantityInputSticky) this.quantityInputSticky.value = min;
  
          publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
        }
  
        fetchQuantityRules() {
          const currentVariantId = this.productForm?.variantIdInput?.value;
          if (!currentVariantId) return;
  
          //this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
          fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              this.updateQuantityRules(this.dataset.section, html);
            })
            .catch((e) => console.error(e))
            .finally(() => {
              //this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
            });
        }
  
        updateQuantityRules(sectionId, html) {
          if (!this.quantityInput && !this.quantityInputSticky) return;
          this.setQuantityBoundries();
  
          const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);
          const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
          for (let selector of selectors) {
            const current = this.quantityForm?.querySelector(selector);
            const currentSticky = this.quantityFormSticky?.querySelector(selector);
            const updated = quantityFormUpdated.querySelector(selector);
            if(!updated) continue;
            if (!current && !currentSticky) continue;
            if (selector === '.quantity__input') {
              const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
              for (let attribute of attributes) {
                const valueUpdated = updated.getAttribute(attribute);
                if (valueUpdated !== null) {
                  current?.setAttribute(attribute, valueUpdated);
                  currentSticky?.setAttribute(attribute, valueUpdated);
                } else {
                  current?.removeAttribute(attribute);
                  currentSticky?.removeAttribute(attribute);
                }
              }
            } else {
              if(current) current.innerHTML = updated.innerHTML;
              if(currentSticky) currentSticky.innerHTML = updated.innerHTML;
            }
          }
        }
  
        get productForm() {
          return this.querySelector(`product-form`);
        }
  
        get productModal() {
          return document.querySelector(`#ProductModal-${this.dataset.section}`);
        }
  
        get pickupAvailability() {
          return this.querySelector(`pickup-availability`);
        }
  
        get variantSelectors() {
          return this.querySelector('variant-selects[data-primary]');
        }
  
        get variantSelectorsAlt() {
          return this.querySelector('variant-selects[data-sticky]');
        }
  
        get relatedProducts() {
          const relatedProductsSectionId = SectionId.getIdForSection(
            SectionId.parseId(this.sectionId),
            'related-products'
          );
          return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
        }
  
        get quickOrderList() {
          const quickOrderListSectionId = SectionId.getIdForSection(
            SectionId.parseId(this.sectionId),
            'quick_order_list'
          );
          return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
        }
  
        get sectionId() {
          return this.dataset.originalSection || this.dataset.section;
        }
      }
    );
  } 